'use client';
import React, { ChangeEvent, ElementType, useId } from 'react';
import './styles.css';
import { renameDomProps } from '@/utils/sanitizeDomProps';
import {
  baseClasses,
  sizeClasses,
  disabledClasses,
  errorClasses,
  validClasses,
  variantClasses,
} from './const';
import { InputProps } from './types';

export function Input({
  id,
  name,
  type = 'text',
  label,
  placeholder,
  value,
  onChange,
  variant = 'light',
  size = 'medium',
  disabled = false,
  error = false,
  valid = false,
  errorMessage,
  icon,
  ...rest
}: InputProps) {
  const InputComponent: ElementType =
    type === 'textarea' ? 'textarea' : 'input';
  const isCheckboxOrRadio = type === 'checkbox' || type === 'radio';
  const generatedId = useId();
  const inputId = id ?? name ?? generatedId;
  const errorId = `${inputId}-error`;

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const inputValue = isCheckboxOrRadio
      ? (event.target as HTMLInputElement).checked
      : event.target.value;
    onChange?.(inputValue);
  };

  const getInputClassNames = () =>
    [
      baseClasses,
      sizeClasses[size],
      disabled
        ? disabledClasses
        : error
          ? errorClasses
          : valid
            ? validClasses
            : variantClasses[variant],
      isCheckboxOrRadio ? 'accent-brand-700 w-4 h-4' : '',
    ].join(' ');

  const getLabelClassNames = () =>
    `mb-1 text-sm font-medium ${
      disabled
        ? 'text-neutral-300'
        : variant === 'dark'
          ? 'text-white'
          : 'text-brand-700'
    }`;

  const renamedProps = renameDomProps(rest);
  const fallbackAriaLabel =
    renamedProps['aria-label'] ?? (label ? undefined : (placeholder ?? name));

  return (
    <div
      className={`my-0 flex ${
        isCheckboxOrRadio
          ? 'input-checkbox-radio flex-row-reverse items-start justify-center gap-2'
          : 'flex-col'
      }`}
    >
      {label && (
        <label htmlFor={inputId} className={getLabelClassNames()}>
          {label}
        </label>
      )}
      <div className={`relative ${type === 'checkbox' ? 'mt-1' : ''}`}>
        {icon && (
          <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
            {icon}
          </span>
        )}
        <InputComponent
          id={inputId}
          name={name}
          placeholder={placeholder}
          value={value as string}
          onChange={handleChange}
          className={`${getInputClassNames()} w-full`}
          disabled={disabled}
          aria-invalid={error}
          aria-describedby={error && errorMessage ? errorId : undefined}
          aria-label={fallbackAriaLabel}
          type={type !== 'textarea' ? type : undefined}
          {...renamedProps}
        />
      </div>
      {error && errorMessage && (
        <p id={errorId} className="mt-1 text-sm text-red-100">
          {errorMessage}
        </p>
      )}
    </div>
  );
}
